import type { IConsentChallenge } from './types';

/**
 * Development-only consent fixtures.
 *
 * The real page renders from a signed challenge estuary puts in the query
 * string, which means it can't be viewed until the backend exists. These
 * fixtures stand in so the screen can be designed and reviewed first:
 *
 *   http://local.feathr.app:9005/oauth/consent?fixture=single
 *
 * Gated on `NODE_ENV !== 'production'`, so webpack eliminates the whole module
 * from production bundles — a fixture that shipped would let anyone render a
 * fake consent screen on a Feathr origin, which is a phishing primitive.
 *
 * A fixture never submits: the page intercepts the decision and reports it
 * instead of posting, because there is no valid challenge for estuary to verify.
 */

export const FIXTURE_NAMES = ['single', 'multi', 'unverified', 'no-accounts', 'expired'] as const;

export type TFixtureName = (typeof FIXTURE_NAMES)[number];

export function areFixturesEnabled(): boolean {
  return process.env.NODE_ENV !== 'production';
}

const PLAYGROUND = { id: '5589c9c30a6540a7a33d3838', name: 'Feathr Playground' };

const ZAPIER: IConsentChallenge['client'] = {
  name: 'Zapier',
  redirect_host: 'zapier.com',
};

/** An hour out, so `single`/`multi` never expire mid-review. */
function soon(): number {
  return Math.floor(Date.now() / 1000) + 3600;
}

const FIXTURES: Record<TFixtureName, () => IConsentChallenge> = {
  // The common case: nothing to choose, so the account is context not a control.
  single: () => ({
    client: ZAPIER,
    scopes: ['persons:read', 'persons:write', 'tags:read', 'custom_fields:read'],
    accounts: [PLAYGROUND],
    default_account_id: PLAYGROUND.id,
    exp: soon(),
  }),

  // Several accounts: the radio group appears, preselected to the default.
  multi: () => ({
    client: ZAPIER,
    scopes: ['persons:read', 'persons:write', 'tags:read', 'custom_fields:read'],
    accounts: [
      PLAYGROUND,
      { id: '62f1a4c9d1e2b30012a45671', name: 'Riverside Arts Council' },
      { id: '63b7e0115ca9f10012bb0042', name: 'Gulf Coast Symphony' },
    ],
    default_account_id: PLAYGROUND.id,
    exp: soon(),
  }),

  unverified: () => ({
    client: {
      name: 'Donor Sync Tool',
      redirect_host: 'donorsync.example.com',
    },
    scopes: ['persons:read', 'persons:write'],
    accounts: [PLAYGROUND],
    default_account_id: PLAYGROUND.id,
    exp: soon(),
  }),

  'no-accounts': () => ({
    client: ZAPIER,
    scopes: ['persons:read'],
    accounts: [],
    default_account_id: '',
    exp: soon(),
  }),

  expired: () => ({
    client: ZAPIER,
    scopes: ['persons:read'],
    accounts: [PLAYGROUND],
    default_account_id: PLAYGROUND.id,
    exp: Math.floor(Date.now() / 1000) - 60,
  }),
};

export function getFixture(name: string): IConsentChallenge | undefined {
  if (!areFixturesEnabled()) {
    return undefined;
  }
  return FIXTURES[name as TFixtureName]?.();
}
